import { useState, useEffect, useRef } from 'react';
import { bffQuery } from '../lib/bffClient';
import { toEnDigits } from '../lib/utils';
import useDebounce from './useDebounce';
import { PATIENT_PHONES_LIST_FIELDS } from '../lib/patientPhones';

// Clinic-wide patient search — the whole registry, not the day on screen.
//
// The day panel's own filter can only narrow the appointments of the day being shown,
// so a patient who is not booked today is invisible to it: the walk-in at the desk, the
// caller asking about their bill, the person whose نوبت is next Tuesday. This searches
// the clinic's patients instead.
//
// `patientsPage` matches DB-side and clinic-scoped on full name / phone / کد ملی /
// شماره پرونده (see list_patients_by_clinic in patientmanagement). The file number lives
// on the clinic-patient link, so clinic A's «۱۲» can never surface clinic B's patient.
// Exactly what a hit is used for and no more: the four fields it is IDENTIFIED by, the debt
// badge, and keycloakUserId — which EditPatientModal reads to keep an app-verified patient's
// identity fields locked from the first frame, before its own re-read lands. Everything else
// about the patient is fetched by whatever the hit is handed to.
const SEARCH_QUERY = `
  query ReceptionPatientSearch($search: String, $limit: Int) {
    patientsPage(search: $search, limit: $limit) {
      currency
      total
      items {
        currency
        id
        name
        phone
        # Every number: a search matches any of them, and the row says which (PM-0536).
        ${PATIENT_PHONES_LIST_FIELDS}
        nationalId
        fileNumber
        keycloakUserId
        # RIALS still owed to the clinic (0 = settled). The BFF resolves it from one
        # batched accounting call and swallows a billing outage, so it costs the badge,
        # never the search.
        outstandingRial
      }
    }
  }
`;

// Below this a query is not a search: one character of a Persian name matches a large
// slice of the registry, and the desk pays a round-trip per keystroke for noise.
export const MIN_QUERY = 2;

export function usePatientSearch(query, { limit = 8 } = {}) {
  const q = (query || '').trim();
  const active = q.length >= MIN_QUERY;

  // Only a live query is debounced. Clearing the box must empty the results at once —
  // debouncing '' would leave the last patient's name hanging under an empty input.
  const debounced = useDebounce(active ? q : '', 260);

  const [results, setResults] = useState([]);
  const [total, setTotal]     = useState(0);
  const [fetching, setFetching] = useState(false);
  const [error, setError]     = useState(null);

  // Keystrokes race: the read for «رض» must not land on top of the results for «رضایی»
  // just because it was slower. Only the newest request may write.
  const reqId = useRef(0);

  useEffect(() => {
    if (!debounced) {
      setResults([]); setTotal(0); setFetching(false); setError(null);
      return;
    }
    const req = ++reqId.current;
    setFetching(true);
    // Numbers are stored in ASCII digits but typed on a Persian keyboard, and the server
    // matches with ILIKE — «۱۴۰۳» would find nothing. Letters pass through untouched.
    bffQuery(SEARCH_QUERY, { search: toEnDigits(debounced), limit })
      .then((data) => {
        if (req !== reqId.current) return;
        setResults(data?.patientsPage?.items || []);
        setTotal(data?.patientsPage?.total || 0);
        setError(null);
        setFetching(false);
      })
      .catch((err) => {
        if (req !== reqId.current) return;
        console.error('patient search error:', err);
        setResults([]); setTotal(0);
        setError(err);
        setFetching(false);
      });
  }, [debounced, limit]);

  return {
    results,
    total,
    error,
    // True from the first keystroke past MIN_QUERY, not a debounce later — otherwise the
    // panel shows «یافت نشد» for a quarter of a second before it has even asked.
    loading: fetching || (active && debounced !== q),
    active,
  };
}

export default usePatientSearch;
